import { FC, useMemo, useState } from 'react';

import {
  Evidence,
  EvidenceTypes,
} from '@/api/holeInOneClaim/evidence.types.ts';
import FullScreenMediaViewer from '@/components/controls/FullScreenMediaViewer.tsx';
import MediaEvidencePreview from '@/components/layout/holeInOneClaimDetailsPage/MediaEvidencePreview.tsx';
import { evidenceToMediaFile } from '@/utils/mediaFile.util.ts';

type MediaFilesSectionProps = {
  title: string;
  files: ReturnType<typeof evidenceToMediaFile>[];
  startIndex?: number;
  onSelect: (index: number) => void;
  emptyMessage?: string;
};

const MediaFilesSection: FC<MediaFilesSectionProps> = ({
  title,
  files,
  startIndex = 0,
  onSelect,
  emptyMessage = 'No files were submitted.',
}) => (
  <section>
    <h2 className="text-md font-semibold mb-3 tracking-wide">{title}</h2>
    {files.length > 0 ? (
      <MediaEvidencePreview
        files={files}
        onSelect={(idx) => onSelect(startIndex + idx)}
      />
    ) : (
      <p className="text-gray-400 text-sm italic">{emptyMessage}</p>
    )}
  </section>
);

type HoleInOneClaimDetailsMediaProps = {
  mediaEvidence: Evidence[];
};

const HoleInOneClaimDetailsMedia: FC<HoleInOneClaimDetailsMediaProps> = ({
  mediaEvidence,
}) => {
  const { capturedMoments, certificate, allFiles } = useMemo(() => {
    const capturedMomentsMemo = mediaEvidence
      .filter((e) => e.type === EvidenceTypes.CAPTURED_MOMENT)
      .map(evidenceToMediaFile);

    const certificateMemo = mediaEvidence
      .filter((e) => e.type === EvidenceTypes.CERTIFICATE)
      .map(evidenceToMediaFile);

    return {
      capturedMoments: capturedMomentsMemo,
      certificate: certificateMemo,
      allFiles: [...capturedMomentsMemo, ...certificateMemo],
    };
  }, [mediaEvidence]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const onNext = (): void => {
    setSelectedIndex((prev) =>
      prev !== null ? (prev + 1) % allFiles.length : 0
    );
  };

  const onPrev = (): void => {
    setSelectedIndex((prev) =>
      prev !== null ? (prev - 1 + allFiles.length) % allFiles.length : 0
    );
  };

  return (
    <div className="space-y-6 mt-4">
      <MediaFilesSection
        title="Captured Moments"
        files={capturedMoments}
        onSelect={setSelectedIndex}
        emptyMessage="No captured moments were submitted."
      />

      <MediaFilesSection
        title="Hole-in-One Certificate"
        files={certificate}
        startIndex={capturedMoments.length}
        onSelect={setSelectedIndex}
        emptyMessage="No certificate was submitted."
      />

      <FullScreenMediaViewer
        isOpen={selectedIndex !== null}
        files={allFiles}
        currentIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onNext={onNext}
        onPrev={onPrev}
      />
    </div>
  );
};

export default HoleInOneClaimDetailsMedia;
